import React, { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Badge,
  Card,
  Group,
  Stack,
  Table,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { FiSearch, FiUsers } from "react-icons/fi";
import { MULTIPLACER_MODULES_BY_KEY } from "@multiconsult-group/multiplacer-core";
import { useAppSelector } from "../app/hooks";
import { selectHttpProject } from "../projectSelectors";
import { formatShortDate } from "../globalUtils";
import { ProjectMemberDetails, memberRoleLabels as roleLabels } from "./ProjectMemberDetails";

export const ProjectMembersGrid: React.FC = () => {
  const project = useAppSelector(selectHttpProject);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const members = useMemo(() => project?.members ?? [], [project?.members]);
  const selectedMember = members.find((member) => String(member.userId) === selectedId);
  const rows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("nb");
    return members
      .filter((member) =>
        `${member.displayName} ${member.email ?? ""} ${roleLabels[member.role]}`
          .toLocaleLowerCase("nb")
          .includes(query)
      )
      .sort((a, b) =>
        (a.displayName || a.email || "").localeCompare(b.displayName || b.email || "", "nb")
      );
  }, [members, search]);

  if (!project) return null;

  return (
    <Stack gap="md">
      {saved && (
        <Alert
          color="green"
          title="Modultilgang oppdatert"
          withCloseButton
          onClose={() => setSaved(false)}
        >
          Endringene er lagret.
        </Alert>
      )}
      <Card withBorder radius="md" p={0} bg="white">
        <Group justify="space-between" p="lg" gap="md">
          <Group gap="sm">
            <FiUsers size={20} />
            <Text fw={600} size="lg">
              Medlemmer
            </Text>
            <Badge variant="light" color="gray">
              {members.length}
            </Badge>
          </Group>
          <TextInput
            aria-label="Søk etter medlem"
            placeholder="Søk etter navn, e-post eller rolle"
            leftSection={<FiSearch />}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            style={{ width: 320, maxWidth: "100%" }}
          />
        </Group>
        <Table.ScrollContainer minWidth={900}>
          <Table verticalSpacing="md" horizontalSpacing="lg" highlightOnHover withRowBorders>
            <Table.Thead style={{ backgroundColor: "#f4f6f8", color: "#495057" }}>
              <Table.Tr>
                <Table.Th>Navn</Table.Th>
                <Table.Th>E-post</Table.Th>
                <Table.Th>Rolle</Table.Th>
                <Table.Th>Modultilgang</Table.Th>
                <Table.Th style={{ whiteSpace: "nowrap" }}>Lagt til</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((member) => {
                const name = member.displayName?.trim();
                const initials = name
                  ? name
                      .split(/\s+/)
                      .map((part) => part[0])
                      .slice(0, 2)
                      .join("")
                  : "?";
                return (
                  <Table.Tr
                    key={String(member.userId)}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setSaved(false);
                      setSelectedId(String(member.userId));
                    }}
                  >
                    <Table.Td>
                      <Group gap="sm" wrap="nowrap">
                        <Avatar color="blue" radius="xl" size={36}>
                          {initials}
                        </Avatar>
                        <UnstyledButton
                          aria-label={`Vis detaljer for ${name || member.email || "medlem"}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            setSaved(false);
                            setSelectedId(String(member.userId));
                          }}
                        >
                          <Text
                            size="sm"
                            fw={500}
                            c="blue.8"
                            style={{ textDecoration: "underline", textUnderlineOffset: 3 }}
                          >
                            {name || "Navn mangler"}
                          </Text>
                        </UnstyledButton>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{member.email || "Ikke registrert"}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        variant="light"
                        color={member.role === "OWNER" || member.role === "ADMIN" ? "blue" : "gray"}
                      >
                        {roleLabels[member.role]}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={4}>
                        {member.permissions?.modules?.length ? (
                          member.permissions.modules.map((permission) => (
                            <Text key={permission.module} size="xs">
                              {MULTIPLACER_MODULES_BY_KEY[permission.module]?.title ??
                                permission.module}
                            </Text>
                          ))
                        ) : (
                          <Text size="sm" c="dimmed">
                            Ingen modultilganger
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td style={{ whiteSpace: "nowrap" }}>
                      <Text size="sm">
                        {member.addedAt ? formatShortDate(member.addedAt) : "—"}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {!rows.length && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text ta="center" c="dimmed" py="xl">
                      {members.length
                        ? "Ingen medlemmer samsvarer med søket."
                        : "Ingen eksterne medlemmer er lagt til ennå."}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        <Text size="xs" c="dimmed" px="lg" py="sm">
          Viser {rows.length} av {members.length} medlemmer · Prosjekt {project.projectNo}
        </Text>
      </Card>
      {selectedMember && (
        <ProjectMemberDetails
          key={`${project.projectNo}:${selectedId}`}
          member={selectedMember}
          projectNo={project.projectNo}
          onClose={() => setSelectedId(null)}
          onSaved={() => {
            setSelectedId(null);
            setSaved(true);
          }}
        />
      )}
    </Stack>
  );
};
